export interface BotEngineEvent {
  type:
    | "status_changed"
    | "chat_message"
    | "kicked"
    | "stats_update"
    | "plugin_event"
    | "plugin_status"
    | "task_update"
    | "task_history_added"
    | "connection_marker_added"
    | "console_log"
    | "inventory_update"
    | "window_update"
    | "window_closed"
    | "player_state"
    | "msg_received";
  payload: any;
  timestamp: string;
  // Per-subscription counter from BotEventHub, starting at 1 on every new stream.
  seq?: number;
}

export type BotEngineStreamState = "connecting" | "live" | "offline";

export interface BotEngineStreamStatus {
  state: BotEngineStreamState;
  // Bumped whenever the stream may have missed events: it reconnected, or the server
  // dropped events for a subscriber that fell behind and the sequence skipped. Nothing
  // replays those, so a page holding derived state has to refetch when this changes.
  epoch: number;
  // Wall-clock of the last byte the server sent, heartbeats included. Null until the
  // first stream opens.
  lastMessageAt: number | null;
}

export type BotEngineEventHandler = (event: BotEngineEvent) => void;
export type BotEngineStreamHandler = (status: BotEngineStreamStatus) => void;

interface BotStream {
  botId: string;
  eventSource: EventSource | null;
  // Bumped on every open so listeners from a superseded EventSource are ignored.
  generation: number;
  refCount: number;
  eventHandlers: Set<BotEngineEventHandler>;
  streamHandlers: Set<BotEngineStreamHandler>;
  state: BotEngineStreamState;
  epoch: number;
  everLive: boolean;
  // Set the moment a stream is torn down, cleared on the first byte from its replacement.
  // Whatever the server published in between is gone, however briefly the socket was down.
  gapPending: boolean;
  lastMessageAt: number | null;
  lastSeq: number | null;
  failures: number;
  retryTimer: number | null;
  offlineTimer: number | null;
}

const streamsByBot = new Map<string, BotStream>();

// Grace avoids banner flicker: a single dropped stream that comes straight back is not
// worth telling the user about.
const OFFLINE_GRACE_MS = 3000;

// The server heartbeats every 15s (StreamSseWithHeartbeat), so three missed beats means
// the socket is half-open. EventSource cannot see that by itself: a proxy or a sleeping
// NAT can stop forwarding without ever closing the connection, leaving readyState OPEN
// and the page frozen on whatever it last rendered.
const STALE_MS = 45_000;
const WATCHDOG_INTERVAL_MS = 5000;

const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30_000;
// A backgrounded tab still retries, just rarely: the unread badge and the message sound
// want a live stream, but a browser holding twenty parked tabs must not hammer the API.
const RETRY_MIN_HIDDEN_MS = 30_000;

function isHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function retryDelay(stream: BotStream): number {
  const exponential = Math.min(RETRY_BASE_MS * 2 ** (stream.failures - 1), RETRY_MAX_MS);
  const target = Math.max(exponential, isHidden() ? RETRY_MIN_HIDDEN_MS : 0);
  return Math.round(target * (0.75 + Math.random() * 0.5));
}

function publishStatus(stream: BotStream) {
  const status: BotEngineStreamStatus = {
    state: stream.state,
    epoch: stream.epoch,
    lastMessageAt: stream.lastMessageAt,
  };
  for (const handler of stream.streamHandlers) handler(status);
}

function setStreamState(stream: BotStream, next: BotEngineStreamState) {
  if (stream.state === next) return;
  stream.state = next;
  publishStatus(stream);
}

function markStale(stream: BotStream) {
  stream.epoch++;
  publishStatus(stream);
}

function isRegistered(stream: BotStream): boolean {
  return streamsByBot.get(stream.botId) === stream;
}

function clearTimers(stream: BotStream) {
  if (stream.retryTimer != null) {
    clearTimeout(stream.retryTimer);
    stream.retryTimer = null;
  }
  if (stream.offlineTimer != null) {
    clearTimeout(stream.offlineTimer);
    stream.offlineTimer = null;
  }
}

function closeSource(stream: BotStream) {
  stream.generation++;
  if (stream.eventSource) {
    stream.eventSource.close();
    stream.gapPending = true;
  }
  stream.eventSource = null;
}

function onBytes(stream: BotStream) {
  stream.lastMessageAt = Date.now();
  stream.failures = 0;
  if (stream.offlineTimer != null) {
    clearTimeout(stream.offlineTimer);
    stream.offlineTimer = null;
  }
  if (stream.gapPending) {
    stream.gapPending = false;
    markStale(stream);
  }
  stream.everLive = true;
  setStreamState(stream, "live");
}

function open(stream: BotStream) {
  clearTimers(stream);
  closeSource(stream);
  stream.lastSeq = null;

  const generation = stream.generation;
  const isCurrent = () => stream.generation === generation && isRegistered(stream);

  const eventSource = new EventSource(`/api/botengine/${stream.botId}/events`);
  stream.eventSource = eventSource;
  if (!stream.everLive) setStreamState(stream, "connecting");

  eventSource.addEventListener("open", () => {
    if (isCurrent()) onBytes(stream);
  });

  eventSource.addEventListener("heartbeat", () => {
    if (isCurrent()) onBytes(stream);
  });

  eventSource.addEventListener("message", (e: MessageEvent) => {
    if (!isCurrent()) return;
    onBytes(stream);
    let event: BotEngineEvent;
    try {
      event = JSON.parse(e.data);
    } catch {
      return;
    }
    if (typeof event.seq === "number") {
      if (event.seq !== (stream.lastSeq ?? 0) + 1) markStale(stream);
      stream.lastSeq = event.seq;
    }
    for (const handler of stream.eventHandlers) handler(event);
  });

  // EventSource retries on its own only after a clean disconnect. A 401, a 403, a 502 from
  // a restarting backend or any response that is not text/event-stream leaves readyState
  // CLOSED for good, which is how this page used to end up permanently dead. Taking over
  // every error keeps one reconnect path instead of two with different timings.
  eventSource.addEventListener("error", () => {
    if (!isCurrent()) return;
    closeSource(stream);
    stream.failures++;
    if (stream.offlineTimer == null && stream.state !== "offline") {
      stream.offlineTimer = window.setTimeout(() => {
        stream.offlineTimer = null;
        setStreamState(stream, "offline");
      }, OFFLINE_GRACE_MS);
    }
    stream.retryTimer = window.setTimeout(() => {
      stream.retryTimer = null;
      if (isRegistered(stream)) open(stream);
    }, retryDelay(stream));
  });
}

function reconnectNow(stream: BotStream) {
  stream.failures = 0;
  open(stream);
}

function ensureStream(botId: string): BotStream {
  const existing = streamsByBot.get(botId);
  if (existing) return existing;

  const stream: BotStream = {
    botId,
    eventSource: null,
    generation: 0,
    refCount: 0,
    eventHandlers: new Set(),
    streamHandlers: new Set(),
    state: "connecting",
    epoch: 0,
    everLive: false,
    gapPending: false,
    lastMessageAt: null,
    lastSeq: null,
    failures: 0,
    retryTimer: null,
    offlineTimer: null,
  };
  streamsByBot.set(botId, stream);
  open(stream);
  return stream;
}

function release(botId: string, drop: (stream: BotStream) => void) {
  const stream = streamsByBot.get(botId);
  if (!stream) return;
  drop(stream);
  stream.refCount--;
  if (stream.refCount <= 0) {
    clearTimers(stream);
    closeSource(stream);
    streamsByBot.delete(botId);
  }
}

export function subscribeBotEngineEvents(botId: string, handler: BotEngineEventHandler): () => void {
  const stream = ensureStream(botId);
  stream.eventHandlers.add(handler);
  stream.refCount++;
  return () => release(botId, s => s.eventHandlers.delete(handler));
}

export function subscribeBotEngineStream(botId: string, handler: BotEngineStreamHandler): () => void {
  const stream = ensureStream(botId);
  stream.streamHandlers.add(handler);
  stream.refCount++;
  handler({ state: stream.state, epoch: stream.epoch, lastMessageAt: stream.lastMessageAt });
  return () => release(botId, s => s.streamHandlers.delete(handler));
}

export function reconnectBotEngineStream(botId: string): void {
  const stream = streamsByBot.get(botId);
  if (stream) reconnectNow(stream);
}

function replaceStaleStreams() {
  for (const stream of streamsByBot.values()) {
    if (stream.state !== "live" || stream.lastMessageAt == null) continue;
    if (Date.now() - stream.lastMessageAt <= STALE_MS) continue;
    setStreamState(stream, "offline");
    reconnectNow(stream);
  }
}

if (typeof window !== "undefined") {
  window.setInterval(replaceStaleStreams, WATCHDOG_INTERVAL_MS);

  // Timers are throttled or frozen while a tab is backgrounded, and a laptop that
  // suspended comes back with every socket dead, so the watchdog cannot be trusted to
  // have run. Re-check the moment the page is looked at again.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    for (const stream of streamsByBot.values()) {
      if (stream.state !== "live") reconnectNow(stream);
    }
    replaceStaleStreams();
  });

  window.addEventListener("online", () => {
    for (const stream of streamsByBot.values()) reconnectNow(stream);
  });

  window.addEventListener("offline", () => {
    for (const stream of streamsByBot.values()) setStreamState(stream, "offline");
  });
}
